"use client";

import React from "react";
import Link from "next/link";
import { 
  Dumbbell, 
  Sparkles, 
  ArrowRight, 
  Flame, 
  Activity, 
  Shield, 
  Award,
  KanbanSquare
} from "lucide-react";

interface GymHeroProps {
  onOpenTrialPass: () => void;
  onExploreTiers: () => void;
  currency: "INR" | "USD";
}

export default function GymHero({
  onOpenTrialPass,
  onExploreTiers,
  currency,
}: GymHeroProps) {
  return (
    <div className="relative overflow-hidden pt-12 pb-16 lg:pt-20 lg:pb-24 border-b border-stone-800/80 bg-stone-950 text-white">
      {/* Background Ambient Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gradient-to-r from-amber-500/10 via-emerald-500/10 to-transparent blur-3xl pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto space-y-6">
          {/* Top Pill */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-stone-900 border border-stone-800 text-stone-300 text-xs font-semibold shadow-inner">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-amber-400 font-bold uppercase tracking-wider text-[10px]">
              Iron &amp; Pulse Athletic Club
            </span>
            <span className="text-stone-600">•</span>
            <span className="text-stone-400">Bangalore Performance Lab</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-6xl font-black font-heading tracking-tight leading-[1.08] text-white">
            Peak Athletic Conditioning Meets{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-400 via-amber-300 to-emerald-400">
              Clinical Biometrics.
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-sm sm:text-base text-stone-300 leading-relaxed max-w-2xl mx-auto font-normal">
            A private athletic sanctuary engineered for executives, competitive lifters, and longevity athletes. Eleiko competition platforms, InBody 770 diagnostic scans, and sub-zero cryotherapy regeneration.
          </p>

          {/* Hero CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 pt-2">
            <button
              onClick={onOpenTrialPass}
              className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-stone-950 font-black text-xs tracking-wide uppercase transition-all shadow-xl shadow-amber-500/20 hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <Flame className="w-4 h-4 text-stone-950" />
              <span>Claim VIP 3-Day Guest Pass</span>
            </button>

            <button
              onClick={onExploreTiers}
              className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-stone-900/90 hover:bg-stone-800 border border-stone-800 text-stone-200 hover:text-white font-bold text-xs transition-colors flex items-center justify-center gap-2"
            >
              <span>Explore Membership Tiers</span>
              <ArrowRight className="w-3.5 h-3.5 text-stone-400" />
            </button>
          </div>

          {/* Stat Pillars */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-10 border-t border-stone-800/80 text-left">
            <div className="p-3.5 rounded-xl bg-stone-900/50 border border-stone-800/80 space-y-1">
              <div className="flex items-center gap-1.5 text-amber-400 text-xs font-bold">
                <Dumbbell className="w-4 h-4" />
                <span>6 Eleiko Bays</span>
              </div>
              <p className="text-[11px] text-stone-400">Competition IWF calibrated steel plates &amp; bars</p>
            </div>

            <div className="p-3.5 rounded-xl bg-stone-900/50 border border-stone-800/80 space-y-1">
              <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-bold">
                <Activity className="w-4 h-4" />
                <span>InBody 770 Lab</span>
              </div>
              <p className="text-[11px] text-stone-400">Medical-grade segmental lean muscle &amp; fat analytics</p>
            </div>

            <div className="p-3.5 rounded-xl bg-stone-900/50 border border-stone-800/80 space-y-1">
              <div className="flex items-center gap-1.5 text-cyan-400 text-xs font-bold">
                <Shield className="w-4 h-4" />
                <span>-110°C Cryo Spa</span>
              </div>
              <p className="text-[11px] text-stone-400">Sub-zero recovery chamber &amp; Finnish ice plunge</p>
            </div>

            <div className="p-3.5 rounded-xl bg-stone-900/50 border border-stone-800/80 space-y-1">
              <div className="flex items-center gap-1.5 text-amber-400 text-xs font-bold">
                <Award className="w-4 h-4" />
                <span>Master PT Staff</span>
              </div>
              <p className="text-[11px] text-stone-400">CSCS, DPT &amp; Hyrox certified performance faculty</p>
            </div>
          </div>
        </div>

        {/* Real-time CRM Sync Bridge Banner */}
        <div className="mt-12 p-4 rounded-2xl bg-gradient-to-r from-stone-900 via-stone-900/90 to-stone-900 border border-amber-500/30 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
              <KanbanSquare className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-white flex items-center gap-2">
                <span>Interactive Fitness CRM Pipeline Active</span>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </h4>
              <p className="text-[11px] text-stone-400">
                Claim a 3-Day Pass above: watch it flow live into our 6-stage Club Membership CRM with automated PDF passes.
              </p>
            </div>
          </div>

          <Link
            href="/demos/gym/crm"
            className="shrink-0 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-xs transition-colors flex items-center gap-1.5"
          >
            <span>Open Member CRM</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
